/**
 * /app/catalog — витрина стратегий (PULSE-230 фаза 3).
 *
 * Опубликованные админом стратегии карточками: трек 30/90д по
 * каноническому счёту, спарклайн, на скольких аккаунтах работает.
 * Юзер подключает стратегию на свои аккаунты тумблерами: demo — сам
 * (cap 5 активных), реал — только через админа. Ошибки лимитов и прав
 * приходят с бэка и показываются как есть.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Users } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'

const fmt = (v: number | null | undefined, d = 1) =>
  v == null ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(d).replace('-', '−')}`

function useCatalog() {
  return useQuery({
    queryKey: ['catalog'],
    queryFn: async () => (await api.get<any>('/catalog')).data,
    staleTime: 30_000,
    refetchInterval: 120_000,
  })
}

function Spark({ daily }: { daily: Array<{ d: string; pp: number }> | null }) {
  if (!daily || daily.length < 2) {
    return <div className="h-[36px] flex items-center text-2xs text-textFaint">мало данных</div>
  }
  let acc = 0
  const pts = daily.map((x) => (acc += x.pp))
  const min = Math.min(0, ...pts)
  const max = Math.max(0, ...pts)
  const range = max - min || 1
  const W = 260, H = 36
  const step = W / (pts.length - 1)
  const y = (v: number) => 2 + (H - 4) - ((v - min) / range) * (H - 4)
  const line = pts.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const up = pts[pts.length - 1] >= 0
  const color = up ? 'var(--pulse)' : 'var(--rose)'
  const area = `${line} L${W},${y(0).toFixed(1)} L0,${y(0).toFixed(1)} Z`
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
      {min < 0 && max > 0 && (
        <line x1="0" x2={W} y1={y(0)} y2={y(0)} stroke="var(--line-hi)" strokeDasharray="2,3" strokeWidth="1" />
      )}
      <path d={area} fill={up ? 'var(--pulse-faint)' : 'var(--rose-faint)'} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  )
}

function Switch({ on, busy, onToggle }: { on: boolean; busy?: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} disabled={busy} aria-pressed={on}
      className={cn('relative w-[30px] h-[17px] rounded-full border transition-colors align-middle flex-none',
        on ? 'bg-pulse-faint border-pulse' : 'bg-elevated border-line hover:border-lineHi',
        busy && 'opacity-50 cursor-wait')}>
      <span className={cn('absolute top-[2px] w-[11px] h-[11px] rounded-full transition-all',
        on ? 'right-[2px] bg-pulse' : 'left-[2px] bg-textFaint')} />
    </button>
  )
}

export default function Catalog() {
  const { data, isLoading } = useCatalog()
  const qc = useQueryClient()
  const [pending, setPending] = useState<string | null>(null)

  const subscribe = useMutation({
    mutationFn: async (v: { strategy_id: number; account_id: number; active: boolean }) =>
      (await api.post<any>(`/catalog/${v.strategy_id}/subscribe`, {
        account_id: v.account_id, active: v.active,
      })).data,
    onMutate: (v) => setPending(`${v.strategy_id}:${v.account_id}`),
    onError: (e: any) => {
      window.alert(e?.response?.data?.detail ?? 'не получилось изменить подписку')
    },
    onSuccess: (resp) => {
      if (resp.shadow_gated) {
        window.alert('Стратегия в shadow-gate: подписка записана, включится после прохождения гейта.')
      }
    },
    onSettled: () => {
      setPending(null)
      qc.invalidateQueries({ queryKey: ['catalog'] })
    },
  })

  const strategies = (data?.strategies ?? []) as any[]
  const accounts = (data?.accounts ?? []) as any[]

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1280px]">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Каталог стратегий</h1>
        <p className="text-sm text-textDim mt-1">
          Проверенные стратегии системы — подключайте на свои аккаунты.
          Демо доступно сразу{data?.cap ? ` (до ${data.cap} активных на аккаунт)` : ''},
          реальный счёт открывает администратор.
        </p>
      </header>

      {isLoading && <div className="py-16 text-center text-textFaint text-sm">загрузка…</div>}

      {!isLoading && !strategies.length && (
        <div className="bg-surface border border-line rounded-lg px-6 py-14 text-center text-textFaint text-sm">
          в каталоге пока пусто — администратор ещё не опубликовал стратегии
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {strategies.map((s) => {
          const up90 = (s.pp_90 ?? 0) >= 0
          const wr = s.n_90 > 0 ? Math.round((s.wins_90 / s.n_90) * 100) : null
          return (
            <div key={s.id}
              className="bg-surface border border-line rounded-lg shadow-soft p-4 flex flex-col gap-3 transition-colors hover:border-lineHi">
              {/* шапка */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{s.name}</span>
                <span className={cn('pill text-[9.5px] font-bold',
                  s.dir === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose')}>
                  {s.dir === 'long' ? 'LONG' : 'SHORT'}
                </span>
                {s.signal_type && (
                  <span className="pill bg-elevated text-textDim">{s.signal_type}</span>
                )}
                {!s.is_active && (
                  <span className="pill font-sans text-[9.5px] uppercase bg-elevated text-textFaint">пауза</span>
                )}
              </div>

              {s.description && (
                <p className="text-xs text-textDim leading-relaxed line-clamp-2">{s.description}</p>
              )}

              {/* трек */}
              <div className="flex items-baseline gap-4">
                <div>
                  <div className={cn('font-mono text-2xl font-semibold leading-none',
                    up90 ? 'text-pulse' : 'text-rose')}>
                    {fmt(s.pp_90)} <span className="text-xs font-normal">pp</span>
                  </div>
                  <div className="text-2xs text-textFaint mt-1">за 90 дней</div>
                </div>
                <div className="ml-auto text-right text-2xs text-textFaint font-mono space-y-0.5">
                  <div>30д: <span className={cn((s.pp_30 ?? 0) >= 0 ? 'text-pulse' : 'text-rose')}>{fmt(s.pp_30)}</span></div>
                  <div>{s.n_90 ?? 0} сд.{wr != null && ` · WR ${wr}%`}</div>
                  <div>ср/трейд {fmt(s.avg_90, 2)}</div>
                </div>
              </div>

              <Spark daily={s.daily} />

              <div className="flex items-center gap-3 text-2xs text-textFaint">
                {s.lock && (
                  <span className="inline-flex items-center gap-1">
                    <Lock size={10} className="opacity-70" />
                    {(() => { try { return JSON.parse(s.lock).join(', ') } catch { return s.lock } })()}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Users size={10} className="opacity-70" />
                  работает на {s.running_on ?? 0} акк.
                </span>
              </div>

              {/* подключение на мои аккаунты */}
              <div className="border-t border-line pt-3 space-y-2">
                {accounts.map((a) => {
                  const on = s.my?.[String(a.id)] === true
                  const busy = pending === `${s.id}:${a.id}`
                  return (
                    <div key={a.id} className="flex items-center gap-2 text-xs">
                      <span className="text-textDim">{a.label}</span>
                      <span className={cn('pill font-sans text-[9.5px] uppercase',
                        a.demo ? 'bg-elevated text-textFaint' : 'bg-fire-faint text-fire')}>
                        {a.demo ? 'демо' : 'реал'}
                      </span>
                      {!a.has_keys && (
                        <span className="text-2xs text-textFaint">нет ключей</span>
                      )}
                      <span className="ml-auto">
                        <Switch on={on} busy={busy}
                          onToggle={() => subscribe.mutate({
                            strategy_id: s.id, account_id: a.id, active: !on,
                          })} />
                      </span>
                    </div>
                  )
                })}
                {!accounts.length && (
                  <div className="text-2xs text-textFaint">нет активных аккаунтов</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
